import React, { useState, useEffect } from 'react';
import logger from '../lib/logger';
import { 
  CreditCard, 
  Check, 
  Loader2, 
  Zap,
  AlertCircle,
  ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';
import * as api from '@/lib/api';

const plans = [
  {
    id: 'starter',
    name: 'Starter',
    price: 0,
    clips: 20,
    clipsPerWeek: 5,
    accountsPerNetwork: 1,
    totalAccounts: 3,
    features: ['5 clips/semana', '1 conta por rede', 'Analytics básico'],
    popular: false
  },
  {
    id: 'individual',
    name: 'Individual',
    price: 29,
    clips: 50,
    accountsPerNetwork: 1,
    totalAccounts: 3,
    features: ['50 clips/mês', '1 conta por rede', 'Analytics básico'],
    popular: false
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 79,
    clips: 300,
    accountsPerNetwork: 5,
    totalAccounts: 15,
    features: ['300 clips/mês', '5 contas por rede', 'Analytics avançado', 'Otimização com IA', 'Suporte prioritário'],
    popular: true
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 299,
    clips: -1,
    accountsPerNetwork: -1,
    totalAccounts: -1,
    features: ['Clips ilimitados', 'Contas ilimitadas', 'Processamento prioritário', 'Acesso para equipe', 'Relatórios avançados'],
    popular: false
  }
];

export default function Billing() {
  const { user, refreshUser } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const res = await api.getSubscription();
      setSubscription(res.data);
    } catch (error) {
      logger.error('Failed to fetch subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    setCheckoutLoading(planId);
    try {
      const originUrl = window.location.origin;
      const res = await api.createCheckout(planId, originUrl);
      
      // Redirect to Stripe Checkout
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error('Failed to create checkout session');
      setCheckoutLoading(null);
    }
  };

  const handleCancel = async () => {
    if (!confirm('Are you sure you want to cancel your subscription?')) return;
    
    try {
      await api.cancelSubscription();
      toast.success('Subscription cancelled');
      await refreshUser();
      fetchSubscription();
    } catch (error) {
      toast.error('Failed to cancel subscription');
    }
  };

  const currentPlan = subscription?.plan || 'starter';
  const clipsUsed = subscription?.clips_used_month || subscription?.clips_used || 0;
  const clipsLimit = subscription?.clips_limit_month || subscription?.clips_limit || 0;
  const usagePercent = clipsLimit > 0 
    ? (clipsUsed / clipsLimit) * 100 
    : 0;

  return (
    <div className="space-y-8 max-w-6xl mx-auto" data-testid="billing-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-heading font-bold">Billing & Subscription</h1>
        <p className="text-muted-foreground">Manage your subscription and usage</p>
      </div>

      {/* Current Plan */}
      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-heading">Current Plan</CardTitle>
              <CardDescription>Your current subscription details</CardDescription>
            </div>
            <Badge 
              variant={subscription?.status === 'active' ? 'default' : 'outline'}
              className={subscription?.status === 'active' ? 'bg-green-500/20 text-green-500 border-green-500/30' : ''}
            >
              {subscription?.status || 'Free'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-3xl font-heading font-bold capitalize">
                {subscription?.plan_name || 'Free Trial'}
              </p>
              {subscription?.price > 0 && (
                <p className="text-muted-foreground">${subscription?.price}/month</p>
              )}
            </div>
            {subscription?.status === 'active' && (
              <Button variant="outline" onClick={handleCancel}>
                Cancel Subscription
              </Button>
            )}
          </div>

          {/* Usage */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Clips Used This Month</span>
              <span>
                {clipsUsed} / {clipsLimit === -1 ? '∞' : clipsLimit}
              </span>
            </div>
            <Progress value={usagePercent} className="h-2" />
          </div>

          {usagePercent >= 80 && clipsLimit > 0 && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <AlertCircle className="w-5 h-5 text-yellow-500" />
              <p className="text-sm text-yellow-500">
                You&apos;re approaching your clip limit. Consider upgrading your plan.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Available Plans */}
      <div>
        <h2 className="text-xl font-heading font-semibold mb-6">Available Plans</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => {
            const isCurrent = currentPlan === plan.id;
            const isStarter = plan.id === 'starter';
            
            return (
              <Card 
                key={plan.id} 
                className={`bg-card border-border relative transition-all ${
                  plan.popular ? 'border-primary glow-primary' : ''
                } ${isCurrent ? 'ring-2 ring-green-500' : ''}`}
              >
                {plan.popular && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white">
                    Most Popular
                  </Badge>
                )}
                {isCurrent && (
                  <Badge className="absolute -top-3 right-4 bg-green-500 text-white">
                    Current Plan
                  </Badge>
                )}
                <CardHeader className="text-center pb-4">
                  <CardTitle className="text-xl font-heading">{plan.name}</CardTitle>
                  <div className="mt-4">
                    {plan.price === 0 ? (
                      <span className="text-4xl font-bold">Free</span>
                    ) : (
                      <>
                        <span className="text-4xl font-bold">${plan.price}</span>
                        <span className="text-muted-foreground">/month</span>
                      </>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <ul className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={`${plan.name}-feature-${index}`} className="flex items-center gap-3 text-sm">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {isStarter ? (
                    <Button 
                      className="w-full"
                      variant="outline"
                      disabled={true}
                      data-testid={`subscribe-${plan.id}-btn`}
                    >
                      {isCurrent ? 'Current Plan' : 'Default Plan'}
                    </Button>
                  ) : (
                    <Button 
                      className={`w-full ${plan.popular && !isCurrent ? 'glow-primary' : ''}`}
                      variant={isCurrent ? 'outline' : (plan.popular ? 'default' : 'outline')}
                      disabled={isCurrent || checkoutLoading === plan.id}
                      onClick={() => handleSubscribe(plan.id)}
                      data-testid={`subscribe-${plan.id}-btn`}
                    >
                      {checkoutLoading === plan.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : isCurrent ? (
                        'Current Plan'
                      ) : (
                        <>
                          {currentPlan !== 'starter' && plans.findIndex(p => p.id === plan.id) > plans.findIndex(p => p.id === currentPlan) 
                            ? 'Upgrade' 
                            : 'Subscribe'}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Payment History */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg font-heading">Payment History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <CreditCard className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No payment history yet</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
